import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Link2 } from 'lucide-react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

// Drag-and-drop upload: acknowledged only after the server returns 2xx (dedupe surfaced from the response).
const DropUpload = ({ clientId, scope, fy, periodKey, types, onDone }) => {
  const [type, setType] = useState('');
  const [pct, setPct] = useState(null);
  const [over, setOver] = useState(false);
  const send = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('scope_kind', scope);
    if (type) fd.append('document_type', type);
    if (scope === 'period' && fy) fd.append('financial_year', fy);
    if (periodKey) fd.append('period_key', periodKey);
    setPct(0);
    try {
      const { data } = await api.post(`/api/clients/${clientId}/documents/upload`, fd, { onUploadProgress: (e) => setPct(Math.round((e.loaded * 100) / (e.total || 1))) });
      toast.success(data.duplicate_of ? 'You already uploaded this file – linked to the existing copy' : 'Uploaded');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed – nothing was saved'); } finally { setPct(null); }
  };
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); send(e.dataTransfer.files[0]); }}
      className={`rounded-xl border-2 border-dashed p-6 space-y-3 transition-colors ${over ? 'border-primary bg-primary/5' : 'border-border'}`} data-testid={`drop-${scope}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Upload className="w-5 h-5 text-muted-foreground" />
        <span className="text-sm">Drag a file here or</span>
        <label className="text-sm underline cursor-pointer">choose a file<input type="file" className="hidden" onChange={(e) => send(e.target.files[0])} data-testid={`file-input-${scope}`} /></label>
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={type} onChange={(e) => setType(e.target.value)} data-testid={`doc-type-${scope}`}>
          <option value="">Document type…</option>
          {Object.entries(types).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      {pct !== null && <Progress value={pct} data-testid={`upload-progress-${scope}`} />}
    </div>
  );
};

const DocList = ({ docs, onReference, fy }) => (
  <div className="rounded-xl border border-border divide-y divide-border bg-card">
    {docs.length === 0 && <p className="p-4 text-sm text-muted-foreground">No documents yet.</p>}
    {docs.map((d) => (
      <div key={d.id} className="flex items-center justify-between gap-3 p-3" data-testid={`portal-doc-${d.id}`}>
        <div className="min-w-0">
          <a className="text-sm underline truncate block" href="#preview" onClick={async (e) => {
            e.preventDefault();
            const r = await api.get(`/api/documents/${d.id}/preview`, { responseType: 'blob' });
            window.open(URL.createObjectURL(r.data), '_blank');
          }} data-testid={`portal-doc-open-${d.id}`}>{d.filename}</a>
          <p className="text-xs text-muted-foreground">{(d.document_type || d.category || '').replace(/_/g, ' ')} · {new Date(d.uploaded_at).toLocaleDateString('en-AU')}</p>
        </div>
        <div className="flex items-center gap-2">
          {d.referenced && <Badge variant="outline" data-testid={`portal-doc-referenced-${d.id}`}><Link2 className="w-3 h-3 mr-1" />Permanent document</Badge>}
          {onReference && <Button size="sm" variant="ghost" onClick={() => onReference(d.id)} data-testid={`portal-doc-reference-${d.id}`}>Use in {fy}</Button>}
        </div>
      </div>
    ))}
  </div>
);

export default function PortalDocuments() {
  const [meta, setMeta] = useState(null);
  const [me, setMe] = useState(null);
  const [fy, setFy] = useState('');
  const [periodKey, setPeriodKey] = useState('');
  const [perm, setPerm] = useState([]);
  const [annual, setAnnual] = useState([]);

  const load = useCallback(async () => {
    const [m, my] = await Promise.all([api.get('/api/portal/documents/meta'), api.get('/api/portal-access/my')]);
    setMeta(m.data); setMe(my.data);
    const f = fy || m.data.financial_years[1] || m.data.financial_years[0];
    setFy(f);
    const [p, a] = await Promise.all([api.get('/api/portal/documents', { params: { area: 'permanent' } }),
      api.get('/api/portal/documents', { params: { area: 'period', fy: f, period_key: periodKey || undefined } })]);
    setPerm(p.data); setAnnual(a.data);
  }, [fy, periodKey]);
  useEffect(() => { load().catch(() => toast.error('Could not load documents')); }, [load]);
  if (!meta || !me) return null;
  const periods = meta.periods.filter((p) => p.financial_year === fy);
  const reference = (id) => api.post(`/api/portal/documents/${id}/reference`, { financial_year: fy }).then(() => { toast.success(`Linked into ${fy}`); load(); });

  return (
    <div className="p-6 lg:p-10 space-y-10" data-testid="portal-documents-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-headings font-bold">Documents</h1>
        <p className="text-sm text-muted-foreground mt-2">For the entity you're working with. Everything you upload is visible to your accountant.</p>
      </div>
      <section className="space-y-4" data-testid="permanent-area">
        <h2 className="text-base md:text-lg font-semibold">Permanent documents</h2>
        <p className="text-xs text-muted-foreground">Constitution, trust deed and variations, ASIC/ABN registration, SMSF deed, property contracts, depreciation schedules, loan agreements, ID and authorisations.</p>
        <DropUpload clientId={me.current} scope="permanent" types={meta.permanent_types} onDone={load} />
        <DocList docs={perm} onReference={reference} fy={fy} />
      </section>
      <section className="space-y-4" data-testid="annual-area">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-base md:text-lg font-semibold">By financial year / period</h2>
          <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={fy} onChange={(e) => { setFy(e.target.value); setPeriodKey(''); }} data-testid="fy-select">
            {meta.financial_years.map((f) => <option key={f} value={f}>FY {f}</option>)}
          </select>
          {periods.length > 0 && (
            <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={periodKey} onChange={(e) => setPeriodKey(e.target.value)} data-testid="period-select">
              <option value="">Whole year</option>
              {periods.map((p) => <option key={p.period_key} value={p.period_key}>{p.label}</option>)}
            </select>
          )}
        </div>
        <DropUpload clientId={me.current} scope="period" fy={fy} periodKey={periodKey} types={meta.period_types} onDone={load} />
        <DocList docs={annual} />
      </section>
    </div>
  );
}
